export type ServiceToken<T> = symbol & { readonly __type?: T };
export type ServiceLifetime = "singleton" | "transient" | "scoped";
export type RuntimeResolver = Pick<RuntimeContainer, "resolve">;
export type ServiceFactory<T> = (container: RuntimeResolver) => T;

type Registration<T> = {
  token: ServiceToken<T>;
  lifetime: ServiceLifetime;
  factory: ServiceFactory<T>;
  dependencies: ServiceToken<unknown>[];
};

export function createServiceToken<T>(description: string): ServiceToken<T> {
  return Symbol(description) as ServiceToken<T>;
}

export class RuntimeContainer {
  private readonly registrations = new Map<
    ServiceToken<unknown>,
    Registration<unknown>
  >();
  private readonly singletons = new Map<ServiceToken<unknown>, unknown>();
  private readonly scopedInstances = new Map<ServiceToken<unknown>, unknown>();

  constructor(private readonly parent?: RuntimeContainer) {}

  register<T>(input: {
    token: ServiceToken<T>;
    lifetime: ServiceLifetime;
    factory: ServiceFactory<T>;
    dependencies?: ServiceToken<unknown>[];
  }): void {
    if (this.registrations.has(input.token)) {
      throw new Error(
        `Service token already registered: ${String(input.token.description)}`,
      );
    }

    this.registrations.set(input.token, {
      token: input.token,
      lifetime: input.lifetime,
      factory: input.factory as ServiceFactory<unknown>,
      dependencies: input.dependencies ?? [],
    });
  }

  createScope(): RuntimeContainer {
    return new RuntimeContainer(this);
  }

  resolve<T>(token: ServiceToken<T>): T {
    return this.resolveInternal(token, []);
  }

  validate(): void {
    for (const token of this.registrations.keys()) {
      this.validateToken(token, []);
    }
  }

  private validateToken(
    token: ServiceToken<unknown>,
    stack: ServiceToken<unknown>[],
  ): void {
    if (stack.includes(token)) {
      const chain = [...stack, token]
        .map((item) => item.description)
        .join(" -> ");
      throw new Error(`Circular dependency detected: ${chain}`);
    }

    const registration = this.findRegistration(token);

    if (!registration) {
      throw new Error(
        `Missing service registration: ${String(token.description)}`,
      );
    }

    for (const dependency of registration.dependencies) {
      this.validateToken(dependency, [...stack, token]);
    }
  }

  private resolveInternal<T>(
    token: ServiceToken<T>,
    stack: ServiceToken<unknown>[],
  ): T {
    const registration = this.findRegistration(token);

    if (!registration) {
      throw new Error(
        `Missing service registration: ${String(token.description)}`,
      );
    }

    if (stack.includes(token)) {
      const chain = [...stack, token]
        .map((item) => item.description)
        .join(" -> ");
      throw new Error(`Circular dependency detected: ${chain}`);
    }

    if (registration.lifetime === "singleton") {
      const root = this.root();
      if (!root.singletons.has(token)) {
        root.singletons.set(
          token,
          registration.factory(this.createResolver([...stack, token])),
        );
      }
      return root.singletons.get(token) as T;
    }

    if (registration.lifetime === "scoped") {
      if (!this.scopedInstances.has(token)) {
        this.scopedInstances.set(
          token,
          registration.factory(this.createResolver([...stack, token])),
        );
      }
      return this.scopedInstances.get(token) as T;
    }

    return registration.factory(this.createResolver([...stack, token])) as T;
  }

  private createResolver(stack: ServiceToken<unknown>[]): RuntimeResolver {
    return {
      resolve: <T>(token: ServiceToken<T>): T =>
        this.resolveInternal(token, stack),
    };
  }

  private findRegistration<T>(
    token: ServiceToken<T>,
  ): Registration<T> | undefined {
    return (
      (this.registrations.get(token) as Registration<T> | undefined) ??
      this.parent?.findRegistration(token)
    );
  }

  private root(): RuntimeContainer {
    return this.parent?.root() ?? this;
  }
}
